export type OfficialSahContributionCategory = 'clinical' | 'independence' | 'everyday_living';
export type OfficialSahMdmCategory =
  | 'CLINICAL_CARE'
  | 'INDEPENDENCE'
  | 'EVERYDAY_LIVING'
  | 'AT_HM'
  | 'CARE_MANAGEMENT';
export type OfficialSahUnitType = 'hours' | 'trip' | 'item' | 'bill' | 'voucher' | 'meal';
export type OfficialSahFundingSourceCode = 'ON' | 'CM' | 'AT' | 'HM' | 'RC' | 'EL';

export interface OfficialSahServiceCatalogueItem {
  serviceCode: string;
  /** Services Australia ACPP invoice identity. Null means the Statura row is
   * broader than one official claim item and must be resolved by the specialist
   * respite or AT/HM workflow before finance submission. */
  acppServiceIdentifier: string | null;
  displayName: string;
  serviceType: string;
  serviceTypeLabel: string;
  contributionCategory: OfficialSahContributionCategory;
  mdmCategory: OfficialSahMdmCategory;
  unitType: OfficialSahUnitType;
  fundingSourceCode: OfficialSahFundingSourceCode;
  sortOrder: number;
  clinicalCategoryFrom?: string;
}

export const SAH_SERVICE_LIST_SOURCE = {
  title: 'Support at Home service list',
  url: 'https://www.health.gov.au/sites/default/files/2025-08/support-at-home-service-list.docx',
  pageUrl:
    'https://www.health.gov.au/resources/publications/support-at-home-service-list?language=en',
  publicationDate: '2025-08-04',
  effectiveFrom: '2025-11-01',
  docxSha256: 'a99a34b0edd92d409614d600662b1ba94b61116891c510170678304f919f62a6',
  pdfSha256: '2703f6e85134f36eedfcfbfb4b9ef0c5833a459ea578a589d72f074ff844b272',
} as const;

export const SAH_ACPP_REFERENCE_SOURCE = {
  title: 'Support at Home invoice CSV eKit — SaH Reference Data v1.3',
  pageUrl:
    'https://www.servicesaustralia.gov.au/support-home-invoicing-and-home-care-account-adjustments?context=20',
  workbookPath:
    'docs/regulatory-sources/support-at-home-invoice-csv-ekit/SaH Reference Data_v1.3_eKit.xlsx',
  publicationDate: '2026-02-02',
  workbookSha256: '69ccc5477b95b37763088893474cf9b07313e25b299bbf968974289de40c8ef3',
} as const;

/**
 * Exact internal-catalogue to Services Australia ACPP invoice mappings.
 *
 * The generic respite, assistive-technology and home-modification catalogue
 * rows are deliberately absent because each represents multiple official
 * claim items. The specialised workflows resolve those identities from the
 * selected item instead of guessing from a broad service label.
 */
export const SAH_ACPP_SERVICE_IDENTIFIERS = {
  SAH_EVERYDAY_LIVING_DOMESTIC_GENERAL_CLEANING: 'SERV-0001',
  SAH_EVERYDAY_LIVING_DOMESTIC_LAUNDRY: 'SERV-0002',
  SAH_EVERYDAY_LIVING_DOMESTIC_SHOPPING_ASSISTANCE: 'SERV-0003',
  SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_GARDENING: 'SERV-0004',
  SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_REPAIRS: 'SERV-0005',
  SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_EXPENSES: 'SERV-0006',
  SAH_EVERYDAY_LIVING_MEALS_DELIVERY: 'SERV-0007',
  SAH_EVERYDAY_LIVING_MEALS_PREPARATION: 'SERV-0008',
  SAH_INDEPENDENCE_SOCIAL_GROUP: 'SERV-0009',
  SAH_INDEPENDENCE_SOCIAL_INDIVIDUAL: 'SERV-0010',
  SAH_INDEPENDENCE_SOCIAL_ACCOMPANIED_ACTIVITIES: 'SERV-0011',
  SAH_INDEPENDENCE_SOCIAL_CULTURAL_SUPPORT: 'SERV-0012',
  SAH_INDEPENDENCE_SOCIAL_DIGITAL_EDUCATION_SUPPORT: 'SERV-0013',
  SAH_INDEPENDENCE_SOCIAL_PERSONAL_AFFAIRS_ASSISTANCE: 'SERV-0014',
  SAH_INDEPENDENCE_SOCIAL_PERSONAL_AFFAIRS_EXPENSES: 'SERV-0015',
  SAH_INDEPENDENCE_TRANSPORT_DIRECT: 'SERV-0016',
  SAH_INDEPENDENCE_TRANSPORT_INDIRECT: 'SERV-0017',
  SAH_CARE_MANAGEMENT_HOME_SUPPORT: 'SERV-0018',
  SAH_RESTORATIVE_CARE_MANAGEMENT_HOME_SUPPORT: 'SERV-0019',
  SAH_INDEPENDENCE_PERSONAL_CARE_SELF_CARE_ADL: 'SERV-0020',
  SAH_INDEPENDENCE_PERSONAL_CARE_SELF_ADMIN_MEDICATION: 'SERV-0021',
  SAH_INDEPENDENCE_PERSONAL_CARE_CONTINENCE_NON_CLINICAL: 'SERV-0022',
  SAH_CLINICAL_NURSING_REGISTERED_NURSE: 'SERV-0023',
  SAH_CLINICAL_NURSING_ENROLLED_NURSE: 'SERV-0024',
  SAH_CLINICAL_NURSING_ASSISTANT: 'SERV-0025',
  SAH_CLINICAL_NURSING_CONSUMABLES: 'SERV-0026',
  SAH_CLINICAL_ALLIED_THERAPY_ASSISTANT: 'SERV-0027',
  SAH_CLINICAL_ALLIED_PODIATRIST: 'SERV-0028',
  SAH_CLINICAL_ALLIED_SOCIAL_WORKER: 'SERV-0029',
  SAH_CLINICAL_ALLIED_SPEECH_PATHOLOGIST: 'SERV-0030',
  SAH_CLINICAL_ALLIED_DIETITIAN_NUTRITIONIST: 'SERV-0031',
  SAH_CLINICAL_ALLIED_ATSI_HEALTH_PRACTITIONER: 'SERV-0032',
  SAH_CLINICAL_ALLIED_ATSI_HEALTH_WORKER: 'SERV-0033',
  SAH_CLINICAL_ALLIED_PHYSIOTHERAPIST: 'SERV-0034',
  SAH_CLINICAL_ALLIED_PSYCHOLOGIST: 'SERV-0035',
  SAH_CLINICAL_ALLIED_EXERCISE_PHYSIOLOGIST: 'SERV-0036',
  SAH_CLINICAL_ALLIED_OCCUPATIONAL_THERAPIST: 'SERV-0037',
  SAH_CLINICAL_ALLIED_COUNSELLOR_PSYCHOTHERAPIST: 'SERV-0038',
  SAH_CLINICAL_ALLIED_MUSIC_THERAPIST: 'SERV-0039',
  SAH_INDEPENDENCE_THERAPEUTIC_ACUPUNCTURIST: 'SERV-0040',
  SAH_INDEPENDENCE_THERAPEUTIC_CHIROPRACTOR: 'SERV-0041',
  SAH_INDEPENDENCE_THERAPEUTIC_DIVERSIONAL_THERAPIST: 'SERV-0042',
  SAH_INDEPENDENCE_THERAPEUTIC_REMEDIAL_MASSEUSE: 'SERV-0043',
  SAH_INDEPENDENCE_THERAPEUTIC_ART_THERAPIST: 'SERV-0044',
  SAH_INDEPENDENCE_THERAPEUTIC_OSTEOPATH: 'SERV-0045',
  SAH_CLINICAL_NUTRITION_PRESCRIBED: 'SERV-0047',
  // Compatibility alias used by transitioned HCP and early SAH records.
  PERSONAL_CARE: 'SERV-0020',
} as const satisfies Record<string, `SERV-${string}`>;

export function resolveOfficialSahAcppServiceIdentifier(
  serviceCode: string | null | undefined,
): string | null {
  const normalized = serviceCode?.trim().toUpperCase();
  if (!normalized) return null;
  return (
    SAH_ACPP_SERVICE_IDENTIFIERS[normalized as keyof typeof SAH_ACPP_SERVICE_IDENTIFIERS] ?? null
  );
}

export const SAH_PERSONAL_CARE_FULLY_FUNDED_FROM = '2026-10-01';

function service(
  serviceCode: string,
  displayName: string,
  serviceType: string,
  serviceTypeLabel: string,
  contributionCategory: OfficialSahContributionCategory,
  mdmCategory: OfficialSahMdmCategory,
  unitType: OfficialSahUnitType,
  fundingSourceCode: OfficialSahFundingSourceCode,
  sortOrder: number,
  clinicalCategoryFrom?: string,
): OfficialSahServiceCatalogueItem {
  return {
    serviceCode,
    acppServiceIdentifier: resolveOfficialSahAcppServiceIdentifier(serviceCode),
    displayName,
    serviceType,
    serviceTypeLabel,
    contributionCategory,
    mdmCategory,
    unitType,
    fundingSourceCode,
    sortOrder,
    ...(clinicalCategoryFrom ? { clinicalCategoryFrom } : {}),
  };
}

export const OFFICIAL_SAH_SERVICE_CATALOGUE: readonly OfficialSahServiceCatalogueItem[] = [
  service(
    'SAH_CLINICAL_NURSING_REGISTERED_NURSE',
    'Registered nurse',
    'registered_nurse',
    'Nursing care',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    101,
  ),
  service(
    'SAH_CLINICAL_NURSING_ENROLLED_NURSE',
    'Enrolled nurse',
    'enrolled_nurse',
    'Nursing care',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    102,
  ),
  service(
    'SAH_CLINICAL_NURSING_ASSISTANT',
    'Nursing assistant',
    'nursing_assistant',
    'Nursing care',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    103,
  ),
  service(
    'SAH_CLINICAL_NURSING_CONSUMABLES',
    'Nursing care consumables',
    'nursing_care_consumables',
    'Nursing care',
    'clinical',
    'CLINICAL_CARE',
    'item',
    'ON',
    104,
  ),
  service(
    'SAH_CLINICAL_ALLIED_ATSI_HEALTH_PRACTITIONER',
    'Aboriginal and Torres Strait Islander health practitioner',
    'aboriginal_torres_strait_islander_health_practitioner',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    201,
  ),
  service(
    'SAH_CLINICAL_ALLIED_ATSI_HEALTH_WORKER',
    'Aboriginal and Torres Strait Islander health worker',
    'aboriginal_torres_strait_islander_health_worker',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    202,
  ),
  service(
    'SAH_CLINICAL_ALLIED_THERAPY_ASSISTANT',
    'Allied health therapy assistant',
    'allied_health_therapy_assistant',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    203,
  ),
  service(
    'SAH_CLINICAL_ALLIED_COUNSELLOR_PSYCHOTHERAPIST',
    'Counsellor or psychotherapist',
    'counsellor_psychotherapist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    204,
  ),
  service(
    'SAH_CLINICAL_ALLIED_DIETITIAN_NUTRITIONIST',
    'Dietitian or nutritionist',
    'dietitian_nutritionist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    205,
  ),
  service(
    'SAH_CLINICAL_ALLIED_EXERCISE_PHYSIOLOGIST',
    'Exercise physiologist',
    'exercise_physiologist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    206,
  ),
  service(
    'SAH_CLINICAL_ALLIED_MUSIC_THERAPIST',
    'Music therapist',
    'music_therapist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    207,
  ),
  service(
    'SAH_CLINICAL_ALLIED_OCCUPATIONAL_THERAPIST',
    'Occupational therapist',
    'occupational_therapist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    208,
  ),
  service(
    'SAH_CLINICAL_ALLIED_PHYSIOTHERAPIST',
    'Physiotherapist',
    'physiotherapist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    209,
  ),
  service(
    'SAH_CLINICAL_ALLIED_PODIATRIST',
    'Podiatrist',
    'podiatrist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    210,
  ),
  service(
    'SAH_CLINICAL_ALLIED_PSYCHOLOGIST',
    'Psychologist',
    'psychologist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    211,
  ),
  service(
    'SAH_CLINICAL_ALLIED_SOCIAL_WORKER',
    'Social worker',
    'social_worker',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    212,
  ),
  service(
    'SAH_CLINICAL_ALLIED_SPEECH_PATHOLOGIST',
    'Speech pathologist',
    'speech_pathologist',
    'Allied health and other therapeutic services',
    'clinical',
    'CLINICAL_CARE',
    'hours',
    'ON',
    213,
  ),
  service(
    'SAH_CLINICAL_NUTRITION_PRESCRIBED',
    'Prescribed nutrition',
    'prescribed_nutrition',
    'Nutrition',
    'clinical',
    'CLINICAL_CARE',
    'item',
    'ON',
    301,
  ),
  service(
    'SAH_CARE_MANAGEMENT_HOME_SUPPORT',
    'Home support care management',
    'home_support_care_management',
    'Care management',
    'clinical',
    'CARE_MANAGEMENT',
    'bill',
    'CM',
    401,
  ),
  service(
    'SAH_RESTORATIVE_CARE_MANAGEMENT_HOME_SUPPORT',
    'Home support restorative care management',
    'home_support_restorative_care_management',
    'Restorative care management',
    'clinical',
    'CARE_MANAGEMENT',
    'bill',
    'RC',
    501,
  ),
  service(
    'SAH_INDEPENDENCE_PERSONAL_CARE_SELF_CARE_ADL',
    'Assistance with self-care and activities of daily living',
    'assistance_self_care_activities_daily_living',
    'Personal care',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    601,
    SAH_PERSONAL_CARE_FULLY_FUNDED_FROM,
  ),
  service(
    'SAH_INDEPENDENCE_PERSONAL_CARE_SELF_ADMIN_MEDICATION',
    'Assistance with the self-administration of medication',
    'assistance_self_administration_medication',
    'Personal care',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    602,
    SAH_PERSONAL_CARE_FULLY_FUNDED_FROM,
  ),
  service(
    'SAH_INDEPENDENCE_PERSONAL_CARE_CONTINENCE_NON_CLINICAL',
    'Continence management (non-clinical)',
    'continence_management_non_clinical',
    'Personal care',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    603,
    SAH_PERSONAL_CARE_FULLY_FUNDED_FROM,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_GROUP',
    'Group social support',
    'group_social_support',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    701,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_INDIVIDUAL',
    'Individual social support',
    'individual_social_support',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    702,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_ACCOMPANIED_ACTIVITIES',
    'Accompanied activities',
    'accompanied_activities',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    703,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_CULTURAL_SUPPORT',
    'Cultural support',
    'cultural_support',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    704,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_DIGITAL_EDUCATION_SUPPORT',
    'Digital education and support',
    'digital_education_support',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    705,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_PERSONAL_AFFAIRS_ASSISTANCE',
    'Assistance to maintain personal affairs',
    'assistance_maintain_personal_affairs',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    706,
  ),
  service(
    'SAH_INDEPENDENCE_SOCIAL_PERSONAL_AFFAIRS_EXPENSES',
    'Expenses to maintain personal affairs',
    'expenses_maintain_personal_affairs',
    'Social support and community engagement',
    'independence',
    'INDEPENDENCE',
    'item',
    'ON',
    707,
  ),
  service(
    'SAH_INDEPENDENCE_THERAPEUTIC_ACUPUNCTURIST',
    'Acupuncturist',
    'acupuncturist',
    'Therapeutic services for independent living',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    801,
  ),
  service(
    'SAH_INDEPENDENCE_THERAPEUTIC_CHIROPRACTOR',
    'Chiropractor',
    'chiropractor',
    'Therapeutic services for independent living',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    802,
  ),
  service(
    'SAH_INDEPENDENCE_THERAPEUTIC_DIVERSIONAL_THERAPIST',
    'Diversional therapist',
    'diversional_therapist',
    'Therapeutic services for independent living',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    803,
  ),
  service(
    'SAH_INDEPENDENCE_THERAPEUTIC_REMEDIAL_MASSEUSE',
    'Remedial masseuse',
    'remedial_masseuse',
    'Therapeutic services for independent living',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    804,
  ),
  service(
    'SAH_INDEPENDENCE_THERAPEUTIC_ART_THERAPIST',
    'Art therapist',
    'art_therapist',
    'Therapeutic services for independent living',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    805,
  ),
  service(
    'SAH_INDEPENDENCE_THERAPEUTIC_OSTEOPATH',
    'Osteopath',
    'osteopath',
    'Therapeutic services for independent living',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    806,
  ),
  service(
    'SAH_INDEPENDENCE_RESPITE_CARE',
    'Respite care',
    'respite_care',
    'Respite',
    'independence',
    'INDEPENDENCE',
    'hours',
    'ON',
    901,
  ),
  service(
    'SAH_INDEPENDENCE_TRANSPORT_DIRECT',
    'Direct transport (driver and car provided)',
    'direct_transport_driver_car_provided',
    'Transport',
    'independence',
    'INDEPENDENCE',
    'trip',
    'ON',
    1001,
  ),
  service(
    'SAH_INDEPENDENCE_TRANSPORT_INDIRECT',
    'Indirect transport (taxi or rideshare service vouchers)',
    'indirect_transport_taxi_rideshare_vouchers',
    'Transport',
    'independence',
    'INDEPENDENCE',
    'voucher',
    'ON',
    1002,
  ),
  service(
    'SAH_AT_HM_ASSISTIVE_TECHNOLOGY',
    'Assistive technology',
    'assistive_technology',
    'Assistive technology and home modifications',
    'independence',
    'AT_HM',
    'item',
    'AT',
    1101,
  ),
  service(
    'SAH_AT_HM_HOME_MODIFICATIONS',
    'Home modifications',
    'home_modifications',
    'Assistive technology and home modifications',
    'independence',
    'AT_HM',
    'item',
    'HM',
    1102,
  ),
  service(
    'SAH_EVERYDAY_LIVING_DOMESTIC_GENERAL_CLEANING',
    'General house cleaning',
    'general_house_cleaning',
    'Domestic assistance',
    'everyday_living',
    'EVERYDAY_LIVING',
    'hours',
    'ON',
    1201,
  ),
  service(
    'SAH_EVERYDAY_LIVING_DOMESTIC_LAUNDRY',
    'Laundry services',
    'laundry_services',
    'Domestic assistance',
    'everyday_living',
    'EVERYDAY_LIVING',
    'hours',
    'ON',
    1202,
  ),
  service(
    'SAH_EVERYDAY_LIVING_DOMESTIC_SHOPPING_ASSISTANCE',
    'Shopping assistance',
    'shopping_assistance',
    'Domestic assistance',
    'everyday_living',
    'EVERYDAY_LIVING',
    'hours',
    'ON',
    1203,
  ),
  service(
    'SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_GARDENING',
    'Gardening',
    'gardening',
    'Home maintenance and repairs',
    'everyday_living',
    'EVERYDAY_LIVING',
    'hours',
    'ON',
    1301,
  ),
  service(
    'SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_REPAIRS',
    'Assistance with home maintenance and repairs',
    'assistance_home_maintenance_repairs',
    'Home maintenance and repairs',
    'everyday_living',
    'EVERYDAY_LIVING',
    'hours',
    'ON',
    1302,
  ),
  service(
    'SAH_EVERYDAY_LIVING_HOME_MAINTENANCE_EXPENSES',
    'Expenses for home maintenance and repairs',
    'expenses_home_maintenance_repairs',
    'Home maintenance and repairs',
    'everyday_living',
    'EVERYDAY_LIVING',
    'item',
    'ON',
    1303,
  ),
  service(
    'SAH_EVERYDAY_LIVING_MEALS_PREPARATION',
    'Meal preparation',
    'meal_preparation',
    'Meals',
    'everyday_living',
    'EVERYDAY_LIVING',
    'hours',
    'ON',
    1401,
  ),
  service(
    'SAH_EVERYDAY_LIVING_MEALS_DELIVERY',
    'Meal delivery',
    'meal_delivery',
    'Meals',
    'everyday_living',
    'EVERYDAY_LIVING',
    'meal',
    'ON',
    1402,
  ),
];

export function sahContributionCategoryForServiceAtDate(
  item: Pick<OfficialSahServiceCatalogueItem, 'contributionCategory' | 'clinicalCategoryFrom'>,
  atDate: string,
): OfficialSahContributionCategory {
  if (item.clinicalCategoryFrom && atDate >= item.clinicalCategoryFrom) return 'clinical';
  return item.contributionCategory;
}

export function sahOfficialServiceTypeLabels(): string[] {
  return Array.from(new Set(OFFICIAL_SAH_SERVICE_CATALOGUE.map((item) => item.serviceTypeLabel)));
}
